import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, KeyRound, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const CODES = ["+91", "+1", "+44", "+61", "+971"];

function errText(e, fallback) {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

export default function ClaimNumberDialog({ open, onOpenChange, initialPhone = "", onDone }) {
  const { user, openAuth, refresh } = useAuth();
  const [step, setStep] = useState("start");
  const [code, setCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [statement, setStatement] = useState("");
  const [demoOtp, setDemoOtp] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setStep("start"); setOtp(""); setDemoOtp(""); setStatement("");
      const raw = (initialPhone || "").replace(/\s/g, "");
      const match = CODES.find((c) => raw.startsWith(c));
      if (match) { setCode(match); setPhone(raw.slice(match.length)); }
      else setPhone(raw.replace(/\D/g, ""));
    }
  }, [open, initialPhone]);

  const fullNumber = `${code}${phone.replace(/\D/g, "")}`;

  const start = async () => {
    if (!user) { onOpenChange(false); openAuth("login"); return toast.error("Please log in to claim a number"); }
    if (phone.replace(/\D/g, "").length < 8) return toast.error("Enter a valid mobile number");
    setLoading(true);
    try {
      const res = await api.post("/number/claim/start", { phone: fullNumber });
      setDemoOtp(res.data.demo_otp || "");
      setStep("verify");
      toast.success("Verification code sent");
    } catch (e) { toast.error(errText(e, "Could not send code")); }
    setLoading(false);
  };

  const verify = async () => {
    if (otp.trim().length < 6) return toast.error("Enter the 6-digit code");
    setLoading(true);
    try {
      await api.post("/number/claim/verify", { phone: fullNumber, otp });
      if (statement.trim()) {
        await api.post("/number/statement", { statement });
      }
      await refresh();
      toast.success("Number verified! Your verified badge is now active.");
      onOpenChange(false);
      if (onDone) onDone(fullNumber);
    } catch (e) { toast.error(errText(e, "Verification failed")); }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-white" data-testid="claim-number-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-teal-brand/10 flex items-center justify-center">
              <BadgeCheck className="h-5 w-5 text-teal" />
            </div>
            <DialogTitle className="font-display text-xl">Claim &amp; verify your number</DialogTitle>
          </div>
        </DialogHeader>

        {step === "start" && (
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">
              Verify ownership of a mobile number to attach a verified badge and add a public response to
              disputes listed against it.
            </p>
            <div>
              <Label>Mobile number</Label>
              <div className="flex gap-2">
                <select value={code} onChange={(e) => setCode(e.target.value)}
                  data-testid="claim-country-code"
                  className="rounded-md border border-slate-200 px-2 text-sm">
                  {CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <Input data-testid="claim-phone-input" value={phone}
                  onChange={(e) => setPhone(e.target.value)} placeholder="Mobile number" className="flex-1" />
              </div>
            </div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-500">
              SMS delivery isn't enabled yet, so for now the code is sent to your account email (and shown below
              in demo mode).
            </div>
            <Button className="w-full bg-teal-brand hover:bg-teal-700 text-white" disabled={loading}
              onClick={start} data-testid="claim-start-btn">
              <ShieldCheck className="h-4 w-4 mr-2" /> {loading ? "Sending…" : "Send verification code"}
            </Button>
          </div>
        )}

        {step === "verify" && (
          <div className="space-y-4 pt-2">
            {demoOtp && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="claim-demo-otp">
                Demo code (testing only): <span className="font-mono font-bold">{demoOtp}</span>
              </div>
            )}
            <div>
              <Label>Enter the 6-digit code</Label>
              <Input data-testid="claim-otp-input" value={otp} onChange={(e) => setOtp(e.target.value)}
                maxLength={6} placeholder="______" className="tracking-[0.5em] text-center font-mono text-lg" />
            </div>
            <div>
              <Label>Public response (optional)</Label>
              <Textarea data-testid="claim-statement-input" value={statement} rows={3}
                onChange={(e) => setStatement(e.target.value)}
                placeholder="A short, neutral statement shown on your number's profile (max 500 chars)." />
            </div>
            <Button className="w-full bg-teal-brand hover:bg-teal-700 text-white" disabled={loading}
              onClick={verify} data-testid="claim-verify-btn">
              <KeyRound className="h-4 w-4 mr-2" /> {loading ? "Verifying…" : "Verify number"}
            </Button>
            <button className="text-xs text-slate-500 hover:text-teal" onClick={() => setStep("start")}>Back</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
